import React, { useState, useMemo } from "react";
import { useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "motion/react";
import { Search, SlidersHorizontal, Grid, List, Loader2 } from "lucide-react";
import Layout from "../components/layout/Layout";
import ExpertCard from "../components/ui/ExpertCard";
import FilterSidebar from "../components/ui/FilterSidebar";
import { experienceLevels } from "../data/experts";
import { useExperts } from "../hooks/useExperts";

export default function ExpertsPage() {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const { experts: dbExperts, loading: expertsLoading } = useExperts();
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("rating");
  const [headerHidden, setHeaderHidden] = useState(false);
  const lastScrollY = useRef(0);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(200);
  const [reachedBottom, setReachedBottom] = useState(false);
  const expertsContainerRef = useRef(null);
  const [filters, setFilters] = useState({
    specializations: [],
    experienceLevels: [],
    availableOnly: false,
    priceMin: "",
    priceMax: "",
    personType: "all"
  });

  const resetFilters = () => {
    setFilters({
      specializations: [],
      experienceLevels: [],
      availableOnly: false,
      priceMin: "",
      priceMax: "",
      personType: "all"
    });
    setSearchQuery("");
  };

  useEffect(() => {
    if (headerRef.current) {
      const h = headerRef.current.offsetHeight;
      if (h > 0) setHeaderHeight(h);
    }
  }, [dbExperts]);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const h = headerRef.current.offsetHeight;
        if (h > 0) setHeaderHeight(h);
      }
    };
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    if (currentScrollY < 50) {
      setHeaderHidden(false);
    } else if (currentScrollY > lastScrollY.current) {
      setHeaderHidden(true);
    }
    lastScrollY.current = currentScrollY;

    const docHeight = document.documentElement.scrollHeight;
    const winHeight = window.innerHeight;
    const scrollBottom = currentScrollY + winHeight;
    if (scrollBottom >= docHeight - 80) {
      setReachedBottom(true);
    } else {
      setReachedBottom(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const filteredExperts = useMemo(() => {
    let result = [...dbExperts];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (expert) =>
          expert.name.toLowerCase().includes(query) ||
          expert.title.toLowerCase().includes(query) ||
          expert.specializations.some((s) => s.toLowerCase().includes(query))
      );
    }

    if (filters.personType !== "all") {
      result = result.filter((expert) => expert.type === filters.personType);
    }

    if (filters.specializations.length > 0) {
      result = result.filter((expert) =>
        expert.specializations.some((s) => filters.specializations.includes(s))
      );
    }

    if (filters.experienceLevels.length > 0) {
      result = result.filter((expert) => {
        return filters.experienceLevels.some((levelLabel) => {
          const level = experienceLevels.find((l) => l.label === levelLabel);
          return expert.experience >= level.min && expert.experience <= level.max;
        });
      });
    }

    if (filters.availableOnly) {
      result = result.filter((expert) => expert.available);
    }

    if (filters.priceMin) {
      result = result.filter((expert) => expert.hourlyRate >= Number(filters.priceMin));
    }

    if (filters.priceMax) {
      result = result.filter((expert) => expert.hourlyRate <= Number(filters.priceMax));
    }

    switch (sortBy) {
      case "rating":
        result.sort((a, b) => b.rating - a.rating);
        break;
      case "experience":
        result.sort((a, b) => b.experience - a.experience);
        break;
      case "price-low":
        result.sort((a, b) => a.hourlyRate - b.hourlyRate);
        break;
      case "price-high":
        result.sort((a, b) => b.hourlyRate - a.hourlyRate);
        break;
      case "reviews":
        result.sort((a, b) => (b.reviewsCount || 0) - (a.reviewsCount || 0));
        break;
      default:
        break;
    }

    return result;
  }, [searchQuery, filters, sortBy, dbExperts]);

  const activeFiltersCount =
    filters.specializations.length +
    filters.experienceLevels.length +
    (filters.availableOnly ? 1 : 0) +
    (filters.priceMin ? 1 : 0) +
    (filters.priceMax ? 1 : 0) +
    (filters.personType !== "all" ? 1 : 0);

  return (
    <Layout hideFooter={!reachedBottom}>
      <div id="experts_page" className="min-h-screen">
        <div style={{ height: headerHeight }} className="shrink-0" />
        <section
          ref={headerRef}
          className="py-8 lg:py-12 border-b border-neutral-800/30 fixed left-0 right-0 top-16 lg:top-20 z-30 bg-neutral-950 transition-transform duration-300 ease-in-out"
          style={{
            transform: headerHidden ? `translateY(-100%)` : "translateY(0)"
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl lg:text-4xl font-extralight text-neutral-100 mb-2"
            >
              Сообщество
            </motion.h1>
            <p className="text-lg font-extralight text-neutral-500 mb-6">
              Эксперты и практики из реального бизнеса
            </p>

            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600" />
                <input
                  type="text"
                  placeholder="Поиск по имени или специализации..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-md bg-neutral-900/60 border border-neutral-800/50 text-neutral-100 text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-700 transition-colors"
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setFilterOpen(true)}
                  className="lg:hidden flex items-center gap-2 px-4 py-3 rounded-md bg-neutral-900/60 border border-neutral-800/50 text-neutral-200 text-lg font-extralight"
                >
                  <SlidersHorizontal className="w-5 h-5" />
                  Фильтры
                  {activeFiltersCount > 0 && (
                    <span className="w-6 h-6 rounded-full bg-primary-700 text-white text-lg flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </button>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-3 rounded-md bg-neutral-900/60 border border-neutral-800/50 text-neutral-200 text-lg font-extralight focus:outline-none focus:border-primary-700"
                >
                  <option value="rating">По рейтингу</option>
                  <option value="experience">По опыту</option>
                  <option value="reviews">По отзывам</option>
                  <option value="price-low">Цена: по возрастанию</option>
                  <option value="price-high">Цена: по убыванию</option>
                </select>

                <div className="hidden lg:flex items-center border border-neutral-800/50 rounded-md overflow-hidden">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-3 transition-colors ${
                      viewMode === "grid"
                        ? "bg-primary-700 text-white"
                        : "bg-neutral-900/60 text-neutral-500 hover:text-neutral-200"
                    }`}
                  >
                    <Grid className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-3 transition-colors ${
                      viewMode === "list"
                        ? "bg-primary-700 text-white"
                        : "bg-neutral-900/60 text-neutral-500 hover:text-neutral-200"
                    }`}
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 lg:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 lg:flex lg:flex-col">
            <div className="flex flex-col lg:flex-row gap-8 lg:min-h-0 lg:flex-1">
              <div className="hidden lg:block w-72 flex-shrink-0 lg:overflow-y-auto lg:pr-2 scrollbar-thin">
                <div>
                  <FilterSidebar
                    isDesktop
                    isOpen={true}
                    onClose={() => {}}
                    filters={filters}
                    setFilters={setFilters}
                    onReset={resetFilters}
                  />
                </div>
              </div>

              <FilterSidebar
                isOpen={filterOpen}
                onClose={() => setFilterOpen(false)}
                filters={filters}
                setFilters={setFilters}
                onReset={resetFilters}
              />

              <div className="flex-1 min-w-0 lg:overflow-y-auto lg:pr-1 scrollbar-thin">
                <div ref={expertsContainerRef}>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-lg font-extralight text-neutral-500">
                    Найдено: <span className="text-neutral-200">{filteredExperts.length}</span> участников
                  </p>
                </div>

                {expertsLoading ? (
                  <div className="text-center py-16">
                    <Loader2 className="w-10 h-10 text-primary-500 animate-spin mx-auto mb-4" />
                    <p className="text-lg font-extralight text-neutral-500">
                      Загрузка экспертов...
                    </p>
                  </div>
                ) : filteredExperts.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-md bg-neutral-800/30 flex items-center justify-center">
                      <Search className="w-8 h-8 text-neutral-600" />
                    </div>
                    <h3 className="text-xl font-light text-neutral-200 mb-2">
                      Никого не найдено
                    </h3>
                    <p className="text-lg font-extralight text-neutral-500 mb-6">
                      Попробуйте изменить параметры поиска
                    </p>
                    <button
                      onClick={resetFilters}
                      className="px-6 py-3 rounded-md bg-primary-700 hover:bg-primary-600 text-white text-lg font-extralight transition-colors"
                    >
                      Сбросить фильтры
                    </button>
                  </div>
                ) : (
                  <div
                    className={
                      viewMode === "grid"
                        ? "grid md:grid-cols-2 xl:grid-cols-3 gap-6"
                        : "space-y-4"
                    }
                  >
                    {filteredExperts.map((expert) => (
                      <ExpertCard key={expert.id} expert={expert} />
                    ))}
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}